// TypingActionAreaRedux.tsx - INTEGRATION with GenericConfirmationModal

import React, { useState, ChangeEvent, KeyboardEvent, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { IoMdAttach } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { ReplyIcon, Upload, FileText, Image as ImageIcon, Video, X, Paperclip } from "lucide-react";
import { Spin } from "antd";
import toast from "react-hot-toast";
import { useDropzone } from "react-dropzone";
import logger, { Logger } from '../../utils/logger';

// Redux imports
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  selectSelectedConversation
} from "../../store/slices/conversationsSlice";
import {
  selectReplyContext,
  clearReplyContext,
  sendMessage,
  selectLead
} from "../../store/slices/messagesSlice";
import { selectWhatsappPhoneId } from "../../store/slices/userSlice";
import { setLoadingState, selectLoadingState } from "../../store/slices/uiSlice";

// Import WhatsApp Content Selector
import WhatsAppContentSelector from "./WhatsAppContentSelector";

// Import media validation constants
import {
  DOC_SIZE_LIMIT,
  IMAGE_SIZE_LIMIT,
  VIDEO_SIZE_LIMIT
} from "../template/createTemplate/headerSection/whatsapp-media-validator";
import { isImage, isVideo } from "../../utils/helpers";

// 🔥 IMPORT: Updated multi-file upload hook and UI components
import { useMultiFileUpload } from './multiFileUpload';
import { MultiFileQueue, DragOverlay, FileCountBadge } from './MultiFileUI';

// 🔥 IMPORT: GenericConfirmationModal for multiple files confirmation
import GenericConfirmationModal from "../../components/GenericConfirmationModal";

// Type definitions
interface TypingActionAreaReduxProps {
  scrollToBottom?: () => void;
}

// Main component
const TypingActionAreaRedux: React.FC<TypingActionAreaReduxProps> = ({ scrollToBottom }) => {
  // Redux state and dispatch
  const dispatch = useAppDispatch();
  const selectedConversation = useAppSelector(selectSelectedConversation);
  const replyContext = useAppSelector(selectReplyContext);
  const phoneId = useAppSelector(selectWhatsappPhoneId);
  const isLoading = useAppSelector(selectLoadingState('sendMessage'));
  const currentLead = useAppSelector(selectLead);

  // Local state
  const [reply, setReply] = useState<string>("");
  const [showContentSelector, setShowContentSelector] = useState(false);

  // 🔥 UPDATED: Destructure confirmationModal from the hook
  const {
    files,
    uploading,
    addFiles,
    removeFile,
    setConversation,
    queueCount,
    completedCount,
    confirmationModal  // ← ADD: Get the confirmation modal props
  } = useMultiFileUpload();

  // Ref for textarea
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Prepare lead object for content selector
  const leadForSelector = currentLead || {
    _id: selectedConversation?._id || '',
    name: selectedConversation?.name || '',
    phone: selectedConversation?.mobile || ''
  };

  // Debug logging for component lifecycle and props
  useEffect(() => {
    Logger.log("TypingActionAreaRedux rendered", {
      selectedConversation: selectedConversation?.id,
      replyContext: replyContext?._id,
      phoneId
    });
  }, [selectedConversation, replyContext, phoneId]);

  // Setup conversation for multi-file upload
  useEffect(() => {
    if (selectedConversation && phoneId) {
      setConversation(selectedConversation, phoneId);
      Logger.log("Multi-file conversation set", {
        id: selectedConversation.id,
        mobile: selectedConversation.mobile
      });
    }
  }, [selectedConversation, phoneId, setConversation]);

  // Focus textarea when reply context changes
  useEffect(() => {
    if (replyContext && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [replyContext]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [reply]);

  // Auto-scroll when files complete
  useEffect(() => {
    if (completedCount > 0 && scrollToBottom) {
      setTimeout(scrollToBottom, 300);
    }
  }, [completedCount, scrollToBottom]);

  // Extract message content from message object
  const getMessageContent = (msg: any): string => {
    if (!msg || !msg.message) return '';
    
    const message = msg.message;
    
    if (typeof message === 'string') return message;
    if (typeof message.body === 'string') return message.body;
    if (message.body?.text?.body) return message.body.text.body;
    if (message.text?.body) return message.text.body;
    
    try {
      return JSON.stringify(message);
    } catch (e) {
      return '';
    }
  };

  // File validation helper
  const validateFile = (file: File): boolean => {
    if (isImage(file.name) && file.size > IMAGE_SIZE_LIMIT) {
      toast.error("Image must be less than 5 MB");
      return false;
    }

    if (isVideo(file.name) && file.size > VIDEO_SIZE_LIMIT) {
      toast.error("Video must be less than 16 MB");
      return false;
    }

    if (!isImage(file.name) && !isVideo(file.name) && file.size > DOC_SIZE_LIMIT) {
      toast.error("Document must be less than 16 MB");
      return false;
    }

    return true;
  };

  // 🔥 UPDATED: Multi-file dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      Logger.log('📂 Multi-file drop detected', { 
        accepted: acceptedFiles.length, 
        rejected: rejectedFiles.length 
      });

      // Log rejected files
      rejectedFiles.forEach(rejection => {
        Logger.log('❌ File rejected:', rejection.file.name, rejection.errors);
      });

      // Add accepted files to queue
      if (acceptedFiles.length > 0) {
        // 📢 IMPORTANT: This will trigger confirmation modal for multiple files
        addFiles(acceptedFiles);
        
        if (acceptedFiles.length > 1) {
          logger.log(`🔔 MULTIPLE FILES DETECTED: ${acceptedFiles.length} files - confirmation modal will appear`);
        } else {
          logger.log(`📤 SINGLE FILE: ${acceptedFiles[0].name} - will upload directly`);
        }
      }
    },
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'video/mp4': ['.mp4'],
      'video/3gpp': ['.3gp'],
      'application/pdf': ['.pdf']
    },
    multiple: true,        // Enable multiple files
    noClick: true,
    noKeyboard: true,
    disabled: false
  });

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Get file type for icon display
  const getFileType = (fileName: string): 'image' | 'video' | 'document' => {
    if (isImage(fileName)) return 'image';
    if (isVideo(fileName)) return 'video';
    return 'document';
  };

  // Send message function
  const sendMessageHandler = async () => {
    if (!selectedConversation) {
      Logger.log("Cannot send message - no selected conversation");
      return;
    }

    if (!reply.trim() && queueCount === 0) {
      Logger.log("Cannot send message - no content to send");
      return;
    }
  
    dispatch(setLoadingState({ key: 'sendMessage', value: true }));
  
    try {
      if (reply.trim()) {
        Logger.log("Sending text message", { 
          text: reply,
          replyToMessageId: replyContext?._id,
          phone: selectedConversation.mobile
        });
        
        await dispatch(sendMessage({
          phone: selectedConversation.mobile,
          text: reply,
          replyToMessageId: replyContext?._id,
          phoneId: phoneId || ''
        })).unwrap();
        
        setReply("");
        
        if (replyContext) {
          dispatch(clearReplyContext());
        }
        
        Logger.log("Text message sent successfully");
      }
      
      if (scrollToBottom) {
        setTimeout(scrollToBottom, 300);
      }
    } catch (error) {
      Logger.log("Error sending message:", error);
      toast.error('Failed to send message');
    } finally {
      dispatch(setLoadingState({ key: 'sendMessage', value: false }));
    }
  };

  // Handle keyboard events
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessageHandler();
    }
  };

  // 🔥 UPDATED: Multi-file input handler
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    Logger.log("Files selected via input", { 
      fileCount: selectedFiles.length,
      fileNames: selectedFiles.map(f => f.name)
    });

    if (selectedFiles.length > 0) {
      // 📢 IMPORTANT: This will trigger confirmation modal for multiple files
      addFiles(selectedFiles);
      
      if (selectedFiles.length > 1) {
        logger.log(`🔔 MULTIPLE FILES SELECTED: ${selectedFiles.length} files - confirmation modal will appear`);
      } else {
        logger.log(`📤 SINGLE FILE SELECTED: ${selectedFiles[0].name} - will upload directly`);
      }
    }
    
    // Reset input value
    e.target.value = '';
  };

  // Render reply preview
  const renderReplyPreview = () => {
    if (!replyContext) return null;

    const messageContent = getMessageContent(replyContext);
    
    return (
      <div style={{
        background: 'linear-gradient(90deg, #25d366 0%, #128c7e 100%)',
        borderRadius: '12px',
        padding: '12px',
        margin: '8px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 2px 8px rgba(37, 211, 102, 0.2)'
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.2)',
          borderRadius: '50%',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white'
        }}>
          <ReplyIcon size={16} />
        </div>
        
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            color: 'white',
            fontWeight: '600',
            fontSize: '12px',
            marginBottom: '2px',
            opacity: 0.9
          }}>
            Replying to {replyContext.sender ? 'You' : selectedConversation?.name}
          </div>
          <div style={{
            color: 'white',
            fontSize: '13px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            opacity: 0.8
          }}>
            {messageContent}
          </div>
        </div>
        
        <button
          onClick={() => dispatch(clearReplyContext())}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'white',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
          }}
        >
          <X size={14} />
        </button>
      </div>
    );
  };

  // Main component styles
  const containerStyle: React.CSSProperties = {
    width: '100%',
    minHeight: '80px',
    maxHeight: '250px',
    padding: '16px',
    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
    borderRadius: '16px',
    border: '1px solid #e9ecef',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    transition: 'all 0.3s ease'
  };

  const inputContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '6px',
    background: 'white',
    borderRadius: '12px',
    border: '1px solid #dee2e6',
    padding: '10px',
    minHeight: '50px',
    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease'
  };

  const textareaStyle: React.CSSProperties = {
    flex: 1,
    border: 'none',
    outline: 'none',
    resize: 'none',
    fontSize: '14px',
    lineHeight: '1.4',
    padding: '8px',
    backgroundColor: 'transparent',
    minHeight: '24px',
    maxHeight: '120px',
    overflow: 'auto',
    fontFamily: 'inherit'
  };

  const buttonStyle: React.CSSProperties = {
    border: 'none',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '500',
    padding: '4px 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
    whiteSpace: 'nowrap'
  };

  const smallButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
    color: '#495057',
    border: '1px solid #dee2e6',
    minWidth: '60px',
    height: '24px'
  };

  const sendButtonStyle: React.CSSProperties = {
    border: 'none',
    borderRadius: '8px',
    width: 'auto',
    minWidth: '70px',
    height: '36px',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'all 0.2s ease',
    outline: 'none',
    fontSize: '12px',
    fontWeight: '600',
    background: isLoading 
      ? 'linear-gradient(135deg, #e9ecef, #dee2e6)' 
      : (!reply.trim() && queueCount === 0)
        ? 'linear-gradient(135deg, #e9ecef, #dee2e6)'
        : 'linear-gradient(135deg, #0084ff, #0056b3)',
    color: (!reply.trim() && queueCount === 0) || isLoading ? '#6c757d' : 'white',
    cursor: (!reply.trim() && queueCount === 0) || isLoading ? 'not-allowed' : 'pointer'
  };

  return (
    <div {...getRootProps({ className: 'dropzone-container' })} style={{ width: '100%', position: 'relative' }}>
      <input {...getInputProps()} />
      
      {/* Multi-file drag overlay */}
      <DragOverlay isDragActive={isDragActive} />
      
      <div style={containerStyle}>
        {/* Multi-file queue UI */}
        <MultiFileQueue
          files={files}
          uploading={uploading}
          onRemove={removeFile}
          onClearCompleted={() => {
            const completedFiles = files.filter(f => f.status === 'completed');
            completedFiles.forEach(file => removeFile(file.id));
          }}
        />
        
        {renderReplyPreview()}
        
        <div style={inputContainerStyle}>
          <textarea
            ref={textareaRef}
            placeholder="Type a message..."
            value={reply}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setReply(e.target.value)}
            onKeyDown={handleKeyDown}
            style={textareaStyle}
          />
          
          {/* All buttons in horizontal row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => setShowContentSelector(true)}
              style={smallButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #e9ecef, #dee2e6)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa, #e9ecef)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              title="Select content from templates"
            >
              + Content
            </button>
            
            {/* File input for traditional file upload */}
            <label htmlFor="fileInput">
              <div
                style={{
                  ...smallButtonStyle,
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #e9ecef, #dee2e6)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa, #e9ecef)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                title="Upload files (supports multiple files)"
              >
                + Files
                {/* File count badge */}
                {queueCount > 0 && (
                  <FileCountBadge count={queueCount} uploading={uploading} />
                )}
              </div>
            </label>
            <input
              accept=".jpeg,.png,.jpg,.mp4,.3gp,.pdf"
              type="file"
              id="fileInput"
              multiple                    // Enable multiple files
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            
            {/* Send button */}
            <button
              onClick={sendMessageHandler}
              disabled={(!reply.trim()) || isLoading}
              style={sendButtonStyle}
              onMouseEnter={(e) => {
                if (!isLoading && (reply.trim() || queueCount > 0)) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 3px 10px rgba(0, 132, 255, 0.25)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              title={
                (!reply.trim()) 
                  ? "Type a message" 
                  : isLoading 
                    ? "Sending..." 
                    : "Send message"
              }
            >
              {isLoading ? (
                <>
                  <Spin size="small" />
                  <span>Sending</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} size="sm" />
                  <span>Send</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* WhatsApp Content Selector */}
      <WhatsAppContentSelector
  isOpen={showContentSelector}
  onClose={() => setShowContentSelector(false)}
  onContentSelect={(processedMessage: React.SetStateAction<string>) => {
    logger.log('📨 Content selected for typing area:', processedMessage);
    
    if (reply.trim()) {
      Logger.log("Content selector replacing existing text:", reply, "with:", processedMessage);
    }
    
    setReply(processedMessage);
    setShowContentSelector(false);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }}
  currentLead={leadForSelector}
  addFiles={addFiles} 
/>

      {/* 🔥 NEW: GenericConfirmationModal for multiple files */}
      <GenericConfirmationModal
        showModal={confirmationModal.showModal}
        onConfirmation={confirmationModal.onConfirmation}
        toggleModal={confirmationModal.toggleModal}
        message={confirmationModal.message}
        title={confirmationModal.title}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
        isWarning={false}  // File upload confirmation is not a warning
        style={{ zIndex: 10000 }}  // Ensure modal appears on top
      />
    </div>
  );
};

export default TypingActionAreaRedux;